/**
 * ==========================================================================
 *  AuthContext — zentraler Login-State der App
 * ==========================================================================
 *  Stellt in der gesamten App drei Dinge bereit:
 *    - user   : das Benutzer-Objekt, oder null wenn nicht eingeloggt
 *    - loading: true solange wir beim App-Start noch prüfen
 *    - login(), logout(): die beiden Aktionen
 *
 *  Nutzung in Komponenten:
 *    import { useAuth } from './context/AuthContext';
 *    const { user, login, logout } = useAuth();
 *
 *  Das Context-Provider holt beim App-Start einmal /api/auth/me — wenn
 *  der Server OK sagt, wissen wir: der Cookie ist gültig, der User ist
 *  noch eingeloggt. Ergibt der Call 401, bleiben wir auf der Login-Seite.
 * ==========================================================================
 */

import { createContext, useContext, useEffect, useState } from 'react';
import { api, ApiError } from '../api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // App-Start: haben wir noch eine gültige Session vom letzten Mal?
  useEffect(() => {
    (async () => {
      try {
        const me = await api.get('/api/auth/me');
        setUser(me);
      } catch (err) {
        // 401 = nicht eingeloggt, das ist kein Fehler, nur ein leerer State
        if (!(err instanceof ApiError) || err.status !== 401) {
          console.error('Fehler beim Auth-Check:', err);
        }
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Erster Login-Schritt (E-Mail + Passwort). Rueckgabe:
  //   { twofaRequired: true }               -> zweiter Schritt noetig (Code)
  //   { twofaRequired: false, benutzer }    -> fertig eingeloggt
  async function login(email, passwort) {
    const res = await api.post('/api/auth/login', { email, passwort });
    if (res && res.twofa_required) {
      return { twofaRequired: true };
    }
    setUser(res.benutzer);
    return { twofaRequired: false, benutzer: res.benutzer };
  }

  // Zweiter Login-Schritt: Authenticator-Code bestaetigen.
  async function login2fa(code) {
    const res = await api.post('/api/auth/login/2fa', { code });
    setUser(res.benutzer);
    return res.benutzer;
  }

  // Benutzer neu vom Server laden (z.B. nachdem 2FA ein-/ausgeschaltet wurde).
  async function refreshUser() {
    try {
      const me = await api.get('/api/auth/me');
      setUser(me);
      return me;
    } catch {
      return null;
    }
  }

  async function logout() {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // Selbst wenn der Server nicht erreichbar ist, loggen wir lokal aus
    }
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, login2fa, refreshUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth() muss innerhalb eines <AuthProvider> aufgerufen werden');
  }
  return ctx;
}
